/**
 * Star visual: shader surface (convection cells, starspots, limb darkening,
 * toon banding), a camera-facing additive corona with noise tongues, drifting
 * prominence arcs, and the point light that lights the system.
 *
 * Per-type look parameters key off `planet.starType`; legacy stars (palette
 * only) fall back on their palette name. Colors always come from the palette.
 *
 * Design reference: design_sheets/star_models.html
 */

import * as THREE from 'three';
import type { PlanetState, StarType } from '../types';

// ============================================================
// LOOK PARAMETERS
// ============================================================

interface StarLook {
  cellScale: number;    // granulation frequency
  speed: number;        // flow / corona animation rate
  spots: number;        // starspot amount 0..1
  coronaScale: number;  // corona radius / star radius
  light: number;        // point light intensity
}

const LOOKS: Record<StarType, StarLook> = {
  redDwarf:    { cellScale: 7.0, speed: 0.7,  spots: 1.0, coronaScale: 1.25, light: 1.6 },
  orangeDwarf: { cellScale: 5.5, speed: 1.0,  spots: 0.7, coronaScale: 1.35, light: 2.0 },
  yellowDwarf: { cellScale: 5.0, speed: 1.0,  spots: 0.5, coronaScale: 1.4,  light: 2.2 },
  blueGiant:   { cellScale: 3.5, speed: 1.6,  spots: 0.1, coronaScale: 1.6,  light: 2.8 },
  redGiant:    { cellScale: 2.2, speed: 0.45, spots: 0.4, coronaScale: 1.7,  light: 1.4 },
};

/** Legacy stars (STAR_COLORS) carry no starType; pick a look from the palette name. */
const LEGACY_LOOKS: Record<string, StarType> = { Sol: 'yellowDwarf', Vega: 'blueGiant', Antares: 'redGiant' };

function lookFor(planet: PlanetState): StarLook {
  const type = planet.starType ?? LEGACY_LOOKS[planet.palette.name] ?? 'yellowDwarf';
  return LOOKS[type];
}

export const STAR_BANDS = 4;        // toon steps on the surface (0 = smooth)
export const STAR_CORE_BOOST = 1.4; // pushes the disc center past the bloom threshold
export const STAR_CORONA_INTENSITY = 1.0;

// ============================================================
// GLSL
// ============================================================

const NOISE_GLSL = /* glsl */`
float hash3(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float noise3(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  float n000 = hash3(i + vec3(0.0, 0.0, 0.0));
  float n100 = hash3(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash3(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash3(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash3(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash3(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash3(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash3(i + vec3(1.0, 1.0, 1.0));
  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);
  float nxy0 = mix(nx00, nx10, f.y);
  float nxy1 = mix(nx01, nx11, f.y);
  return mix(nxy0, nxy1, f.z);
}
float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise3(p);
    p = p * 2.02 + vec3(1.7, 9.2, 3.1);
    a *= 0.5;
  }
  return v;
}
`;

const SURFACE_VERT = /* glsl */`
varying vec3 vPos;
varying vec3 vN;
varying vec3 vV;
void main() {
  vPos = position;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = normalize(-mv.xyz);
  gl_Position = projectionMatrix * mv;
}`;

const SURFACE_FRAG = NOISE_GLSL + /* glsl */`
uniform vec3 uCore;
uniform vec3 uCorona;
uniform float uTime;
uniform float uSpeed;
uniform float uBands;
uniform float uBoost;
uniform float uCellScale;
uniform float uSpotAmount;
varying vec3 vPos;
varying vec3 vN;
varying vec3 vV;
void main() {
  vec3 p = normalize(vPos);
  float t = uTime * uSpeed;
  float cells = fbm(p * uCellScale + vec3(0.0, t * 0.07, 0.0));                 // granulation
  float flow = fbm(p * uCellScale * 0.45 + vec3(t * 0.03, 0.0, -t * 0.02));      // slow bright/dark regions
  float v = cells * 0.75 + flow * 0.45;
  if (uBands > 0.5) v = floor(v * uBands) / uBands + 0.5 / uBands;
  float spots = smoothstep(0.60, 0.66, fbm(p * 2.2 + vec3(5.0, t * 0.01, 2.0))) * uSpotAmount;
  vec3 dark = uCorona * 0.6;
  vec3 col = mix(dark, uCore, clamp(v, 0.0, 1.0));
  col = mix(col, dark * 0.45, spots);
  float facing = clamp(dot(normalize(vN), normalize(vV)), 0.0, 1.0);
  col *= mix(0.55, 1.0, pow(facing, 0.45));                                      // limb darkening
  col *= uBoost;
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const CORONA_VERT = /* glsl */`
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const CORONA_FRAG = NOISE_GLSL + /* glsl */`
uniform vec3 uCore;
uniform vec3 uCorona;
uniform float uTime;
uniform float uSpeed;
uniform float uIntensity;
uniform float uDisc;        // star disc radius in quad units (quad spans -1..1)
varying vec2 vUv;
void main() {
  vec2 c = vUv * 2.0 - 1.0;
  float r = length(c);
  float a = atan(c.y, c.x);
  float t = uTime * uSpeed;
  float tongues = fbm(vec3(cos(a) * 2.0, sin(a) * 2.0, t * 0.25));               // seamless around the circle
  float wisps = fbm(vec3(cos(a) * 5.0 + 3.0, sin(a) * 5.0, -t * 0.4));
  float edge = uDisc + 0.08 + tongues * 0.42 + wisps * 0.14;
  float flames = 1.0 - smoothstep(uDisc, edge, r);                               // 1 at the disc, 0 at the noisy edge
  float outside = smoothstep(uDisc - 0.04, uDisc + 0.02, r);                      // nothing over the disc itself
  float halo = exp(-(r - uDisc) * 7.0) * outside;
  float window = 1.0 - smoothstep(0.70, 0.95, r);                                // zero well inside the quad: its edge never shows
  vec3 col = (uCorona * flames * 0.85 + mix(uCorona, uCore, 0.6) * halo * 0.9) * outside * window * uIntensity;
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

// ============================================================
// VISUAL
// ============================================================

interface Prominence {
  pivot: THREE.Group;
  arc: THREE.Mesh;
  phase: number;
  speed: number;
}

export interface StarVisual {
  group: THREE.Group;          // everything, positioned at the star
  surface: THREE.Mesh;
  corona: THREE.Mesh;          // camera-facing quad
  light: THREE.PointLight;
  surfaceMat: THREE.ShaderMaterial;
  coronaMat: THREE.ShaderMaterial;
  prominences: Prominence[];
  spinRate: number;
}

export function createStarVisual(planet: PlanetState): StarVisual {
  const look = lookFor(planet);
  const R = planet.radius;
  const core = new THREE.Color(planet.palette.a);
  const corona = new THREE.Color(planet.palette.b);
  const group = new THREE.Group();
  group.position.set(planet.position.x, planet.position.y, planet.position.z);

  const surfaceMat = new THREE.ShaderMaterial({
    uniforms: {
      uCore: { value: core }, uCorona: { value: corona },
      uTime: { value: 0 }, uSpeed: { value: look.speed },
      uBands: { value: STAR_BANDS }, uBoost: { value: STAR_CORE_BOOST },
      uCellScale: { value: look.cellScale }, uSpotAmount: { value: look.spots },
    },
    vertexShader: SURFACE_VERT,
    fragmentShader: SURFACE_FRAG,
  });
  const surface = new THREE.Mesh(new THREE.IcosahedronGeometry(R, 4), surfaceMat);
  group.add(surface);

  // Corona quad: roomy so tongues and halo die out before its border
  const quadSize = R * 2 * look.coronaScale * 1.7;
  const coronaMat = new THREE.ShaderMaterial({
    uniforms: {
      uCore: { value: core }, uCorona: { value: corona },
      uTime: { value: 0 }, uSpeed: { value: look.speed },
      uIntensity: { value: STAR_CORONA_INTENSITY }, uDisc: { value: R / (quadSize / 2) },
    },
    vertexShader: CORONA_VERT,
    fragmentShader: CORONA_FRAG,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const coronaQuad = new THREE.Mesh(new THREE.PlaneGeometry(quadSize, quadSize), coronaMat);
  group.add(coronaQuad);

  // Prominences: thin additive half-rings bowing out of the surface, drifting around the star
  const promMat = new THREE.MeshBasicMaterial({
    color: corona.clone().lerp(core, 0.35), transparent: true, opacity: 0.55,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const prominences: Prominence[] = [];
  const count = planet.starType === 'blueGiant' ? 2 : 4;
  for (let i = 0; i < count; i++) {
    const arcR = R * (0.18 + 0.12 * ((i * 7) % 3) / 2);
    const arc = new THREE.Mesh(new THREE.TorusGeometry(arcR, R * 0.022, 6, 28, Math.PI), promMat);
    arc.position.set(0, R * 0.96, 0);   // half-ring in XY contains the radial +Y: ends on the surface, bows outward
    const pivot = new THREE.Group();
    pivot.add(arc);
    pivot.rotation.set(i * 1.3, i * 2.1, i * 0.7);
    group.add(pivot);
    prominences.push({ pivot, arc, phase: i * 1.7, speed: 0.15 + 0.07 * i });
  }

  const light = new THREE.PointLight(core, look.light, R * 16);
  group.add(light);

  return { group, surface, corona: coronaQuad, light, surfaceMat, coronaMat, prominences, spinRate: 0.02 * look.speed };
}

/** Per frame: animate the shaders, face the corona at the camera, drift the prominences. */
export function updateStarVisual(v: StarVisual, time: number, camera: THREE.Camera): void {
  v.surfaceMat.uniforms.uTime.value = time;
  v.coronaMat.uniforms.uTime.value = time;
  v.corona.quaternion.copy(camera.quaternion);
  v.surface.rotation.y = time * v.spinRate;
  for (const p of v.prominences) {
    p.pivot.rotation.y += 0.0005 * p.speed;
    p.arc.scale.set(1, 0.85 + 0.25 * Math.sin(time * p.speed * 2.0 + p.phase), 1);
  }
}
